import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ApiService } from "../../api/api-service";

interface BatchLog {
  batch_no: string;
  log_id: string;
  timestamp: string;
  changeType: string;
  description: string;
  oldValue?: string;
  newValue?: string;
  changedBy?: string;
  courseName?: string;
  trainerName?: string;
  admin?: string;
  isRead?: boolean;
}

interface Stats {
  total: number;
  byType: Record<string, number>;
}

const PAGE_SIZE = 15;
const DEBOUNCE_MS = 400;

// Change type display configuration — NO emojis/icons
const CHANGE_TYPE_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  BATCH_CREATED: {
    label: "Batch Created",
    color: "text-indigo-700",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
  },
  BATCH_STATUS_CHANGED: {
    label: "Status Changed",
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  BATCH_ON_HOLD: {
    label: "Batch On Hold",
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
  },
  TRAINER_ADDED: {
    label: "Trainer Added",
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
  },
  TRAINER_REMOVED: {
    label: "Trainer Removed",
    color: "text-red-700",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
  TRAINER_ON_LEAVE: {
    label: "Trainer On Leave",
    color: "text-orange-700",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
  },
  STUDENT_ADDED: {
    label: "Student Added",
    color: "text-teal-700",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
  },
  STUDENT_REMOVED: {
    label: "Student Removed",
    color: "text-rose-700",
    bgColor: "bg-rose-50",
    borderColor: "border-rose-200",
  },
};

const ALL_CHANGE_TYPES = Object.keys(CHANGE_TYPE_CONFIG);
const DAY_OPTIONS = [
  { label: "All Time", value: "" },
  { label: "Last 7 Days", value: "7" },
  { label: "Last 30 Days", value: "30" },
  { label: "Last 90 Days", value: "90" },
];

export default function RecentChanges() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<BatchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<Stats>({ total: 0, byType: {} });

  // Filters — text inputs are debounced before triggering a fetch
  const [batchFilterInput, setBatchFilterInput] = useState("");
  const [trainerFilterInput, setTrainerFilterInput] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [trainerFilter, setTrainerFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [daysFilter, setDaysFilter] = useState("");

  // Server cursor pagination: forward-only cursor + a stack of prior cursors
  // so "Previous" can step back without needing arbitrary page jumps.
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Undo stack — last 5 marked-as-read items
  const [undoStack, setUndoStack] = useState<
    { batch_no: string; log_id: string }[]
  >([]);

  // Debounce text filters
  useEffect(() => {
    const t = setTimeout(() => setBatchFilter(batchFilterInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [batchFilterInput]);

  useEffect(() => {
    const t = setTimeout(() => setTrainerFilter(trainerFilterInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [trainerFilterInput]);

  // Tracks the most recently *fired* request. Network responses don't always
  // arrive in the order they were sent — without this guard, a slower,
  // stale request (e.g. from an earlier keystroke or filter) can resolve
  // AFTER a newer one and silently overwrite the correct, freshly-filtered
  // results with outdated data. Every response checks this before touching
  // state; anything that isn't still the latest request gets discarded.
  const requestIdRef = useRef(0);

  const fetchLogs = useCallback(
    async (targetCursor: string | null) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", String(PAGE_SIZE));
        if (targetCursor) params.set("cursor", targetCursor);
        if (batchFilter) params.set("batch_no", batchFilter);
        if (typeFilter) params.set("changeType", typeFilter);
        if (trainerFilter) params.set("trainerName", trainerFilter);
        if (daysFilter) params.set("days", daysFilter);

        const res = await ApiService.getInstance().get(
          `/api/recent-changes?${params.toString()}`,
        );

        // Discard if a newer request has been fired since this one started.
        if (requestId !== requestIdRef.current) return;

        setLogs(res.data.data || []);
        setNextCursor(res.data.nextCursor || null);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error("Error fetching recent changes:", error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [batchFilter, typeFilter, trainerFilter, daysFilter],
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await ApiService.getInstance().get("/api/recent-changes/stats");
      setStats({ total: res.data.total || 0, byType: res.data.byType || {} });
    } catch (error) {
      console.error("Error fetching recent-changes stats:", error);
    }
  }, []);

  // Whenever filters change, reset to page 1 (no cursor, empty stack)
  useEffect(() => {
    setCursor(null);
    setCursorStack([]);
    fetchLogs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchFilter, typeFilter, trainerFilter, daysFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchLogs(cursor), fetchStats()]);
    setTimeout(() => setRefreshing(false), 600);
  };

  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, cursor]);
    setCursor(nextCursor);
    fetchLogs(nextCursor);
  };

  const handlePrevPage = () => {
    if (cursorStack.length === 0) return;
    const prevStack = [...cursorStack];
    const prevCursor = prevStack.pop() as string | null;
    setCursorStack(prevStack);
    setCursor(prevCursor);
    fetchLogs(prevCursor);
  };

  // Mark as Read
  const handleMarkRead = async (batch_no: string, log_id: string) => {
    try {
      await ApiService.getInstance().put("/api/recent-changes/mark-read", {
        batch_no,
        log_id,
      });
      setLogs((prev) =>
        prev.map((l) =>
          l.batch_no === batch_no && l.log_id === log_id
            ? { ...l, isRead: true }
            : l,
        ),
      );
      setUndoStack((prev) => [{ batch_no, log_id }, ...prev].slice(0, 5));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  // Undo (mark unread)
  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const [latest, ...rest] = undoStack;
    try {
      await ApiService.getInstance().put("/api/recent-changes/mark-unread", {
        batch_no: latest.batch_no,
        log_id: latest.log_id,
      });
      setLogs((prev) =>
        prev.map((l) =>
          l.batch_no === latest.batch_no && l.log_id === latest.log_id
            ? { ...l, isRead: false }
            : l,
        ),
      );
      setUndoStack(rest);
    } catch (error) {
      console.error("Error undoing read:", error);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const getConfig = (changeType: string) =>
    CHANGE_TYPE_CONFIG[changeType] || {
      label: changeType,
      color: "text-gray-700",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
    };

  const createdCount = stats.byType["BATCH_CREATED"] || 0;
  const statusCount =
    (stats.byType["BATCH_STATUS_CHANGED"] || 0) + (stats.byType["BATCH_ON_HOLD"] || 0);
  const trainerCount =
    (stats.byType["TRAINER_ADDED"] || 0) +
    (stats.byType["TRAINER_REMOVED"] || 0) +
    (stats.byType["TRAINER_ON_LEAVE"] || 0);
  const studentCount =
    (stats.byType["STUDENT_ADDED"] || 0) + (stats.byType["STUDENT_REMOVED"] || 0);

  const hasActiveFilters = batchFilterInput || typeFilter || trainerFilterInput || daysFilter;
  const currentPageNumber = cursorStack.length + 1;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* ── TOP HEADER ── */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #221E45 0%, #3b2d7a 50%, #5a3fbe 100%)",
        }}
        className="text-white px-8 py-4 shadow-lg"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/Batches")}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 text-white border border-white/20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                Recent Changes
              </h1>
              <p className="text-xs text-white/60 mt-0.5">
                SuperAdmin view — Track all batch modifications ({stats.total.toLocaleString()} total records)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Stat pills — live counters, not derived from the current page */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-indigo-500/20 border border-indigo-400/40 rounded-full px-4 py-1.5">
                <span className="text-sm font-semibold text-indigo-300">
                  {createdCount} Created
                </span>
              </div>
              <div className="flex items-center gap-2 bg-blue-500/20 border border-blue-400/40 rounded-full px-4 py-1.5">
                <span className="text-sm font-semibold text-blue-300">
                  {statusCount} Status
                </span>
              </div>
              <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-4 py-1.5">
                <span className="text-sm font-semibold text-emerald-300">
                  {trainerCount} Trainer
                </span>
              </div>
              <div className="flex items-center gap-2 bg-teal-500/20 border border-teal-400/40 rounded-full px-4 py-1.5">
                <span className="text-sm font-semibold text-teal-300">
                  {studentCount} Student
                </span>
              </div>
            </div>
            {/* Undo button */}
            {undoStack.length > 0 && (
              <button
                onClick={handleUndo}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold hover:bg-yellow-500/30 transition-all"
                title={`Undo last read (${undoStack.length} remaining)`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4"
                  />
                </svg>
                Undo ({undoStack.length})
              </button>
            )}
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 border border-white/20"
              title="Refresh"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-4 h-4 text-white ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* ── FILTER BAR ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 mr-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                />
              </svg>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Filters (server-side)
              </span>
            </div>

            {/* Batch No */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search Batch No..."
                value={batchFilterInput}
                onChange={(e) => setBatchFilterInput(e.target.value)}
                className="pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 w-48 placeholder:text-slate-400 text-slate-700"
              />
              {batchFilterInput && (
                <button
                  onClick={() => setBatchFilterInput("")}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Trainer Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search Trainer..."
                value={trainerFilterInput}
                onChange={(e) => setTrainerFilterInput(e.target.value)}
                className="pl-8 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 w-48 placeholder:text-slate-400 text-slate-700"
              />
              {trainerFilterInput && (
                <button
                  onClick={() => setTrainerFilterInput("")}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Change Type Dropdown */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 text-slate-700 bg-white cursor-pointer"
            >
              <option value="">All Change Types</option>
              {ALL_CHANGE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {CHANGE_TYPE_CONFIG[type].label}
                </option>
              ))}
            </select>

            {/* Time Range Dropdown */}
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(e.target.value)}
              className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#221E45]/20 focus:border-[#221E45]/40 text-slate-700 bg-white cursor-pointer"
            >
              {DAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Clear All */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setBatchFilterInput("");
                  setTrainerFilterInput("");
                  setTypeFilter("");
                  setDaysFilter("");
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear All
              </button>
            )}

            {/* Page indicator */}
            <div className="ml-auto">
              <span className="text-xs text-slate-400">
                Page <span className="font-semibold text-slate-600">{currentPageNumber}</span>
              </span>
            </div>
          </div>
        </div>

        {/* ── TABLE ── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-8 h-8 border-[3px] border-[#221E45]/20 border-t-[#221E45] rounded-full animate-spin"></div>
              <p className="text-sm text-slate-400">
                Loading recent changes...
              </p>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-2">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-7 h-7 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <p className="text-base font-semibold text-slate-600">
                No changes found
              </p>
              <p className="text-sm text-slate-400">
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "Changes will appear here as batches are modified"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Batch No
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Date & Time
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Change Type
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5">
                      Description
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Course
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 whitespace-nowrap">
                      Admin
                    </th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3.5 whitespace-nowrap">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => {
                    const config = getConfig(log.changeType);
                    const isRead = log.isRead === true;
                    return (
                      <tr
                        key={`${log.batch_no}-${log.log_id}`}
                        className={`transition-colors duration-150 ${
                          isRead
                            ? "bg-slate-50/70 opacity-60"
                            : "bg-white hover:bg-slate-50/60"
                        }`}
                      >
                        {/* Batch No */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-1.5 h-8 rounded-full flex-shrink-0 ${isRead ? "bg-slate-200" : "bg-[#221E45]/20"}`}
                            ></div>
                            <span
                              className={`font-bold text-sm ${isRead ? "text-slate-400" : "text-[#221E45]"}`}
                            >
                              {log.batch_no}
                            </span>
                          </div>
                        </td>
                        {/* Date & Time */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="text-sm text-slate-700 font-medium">
                              {formatDate(log.timestamp)}
                            </span>
                            <span className="text-xs text-slate-400">
                              {formatTime(log.timestamp)}
                            </span>
                          </div>
                        </td>
                        {/* Change Type Badge */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.bgColor} ${config.color} border ${config.borderColor}`}
                          >
                            {config.label}
                          </span>
                        </td>
                        {/* Description */}
                        <td className="px-6 py-4 max-w-xs">
                          <span className="text-sm text-slate-700 line-clamp-2">
                            {log.description}
                          </span>
                        </td>
                        {/* Course */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-slate-600">
                            {log.courseName || "—"}
                          </span>
                        </td>
                        {/* Admin */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {log.admin ? (
                              <>
                                <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-[10px] font-bold text-violet-700">
                                    {log.admin.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm text-slate-600 font-medium">
                                  {log.admin}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </div>
                        </td>
                        {/* Mark as Read Action */}
                        <td className="px-4 py-4 text-center whitespace-nowrap">
                          {isRead ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-400 border border-slate-200">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              Read
                            </span>
                          ) : (
                            <button
                              onClick={() =>
                                handleMarkRead(log.batch_no, log.log_id)
                              }
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#221E45] text-white hover:bg-[#2d2760] transition-all duration-200 shadow-sm hover:shadow"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3 h-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                              Mark Read
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── PAGINATION (server cursor-based: Prev / Next only) ── */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50/50">
            <p className="text-xs text-slate-500">
              Showing {logs.length} record{logs.length !== 1 ? "s" : ""} on this page
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={cursorStack.length === 0 || loading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </button>
              <span className="text-xs font-semibold text-slate-600 px-2">
                Page {currentPageNumber}
              </span>
              <button
                onClick={handleNextPage}
                disabled={!nextCursor || loading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}